import React from 'react';
import './LowboyTimesheet.scss';
import {
    Card,
    CardBody,
    CardHeader,
    CardSubtitle,
    FormGroup,
    Input,
} from 'reactstrap';
import { FormLabel } from '../common/forms/FormElements';
import moment from 'moment';
import { MomentDateFormat } from '../MomentDateFormat';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faExclamationTriangle,
    faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import { isLowboyTimesheetContractValid } from './LowboyTimesheet';
import cls from 'classnames';
import LowboyTimesheetDetailCard from './LowboyTimesheetDetailCard';
import AdjustmentDetail from '../crewTimesheet/AdjustmentDetail';
import { TimesheetStatus } from '../timesheet/Timesheet';

export default class LowboyTimesheetContract extends React.Component {
    onStartTimeChange = (e) => {
        const inputStartTime = e.target.value;

        const startTime = inputStartTime
            ? moment(inputStartTime).format(MomentDateFormat.DateTimeLocalInput)
            : '';

        const lowboyTimesheetContract = this.getLowboyTimesheetContract();

        lowboyTimesheetContract.startTime = startTime;

        this.signalChange(lowboyTimesheetContract);
    };

    onEndTimeChange = (e) => {
        const inputEndTime = e.target.value;

        const endTime = inputEndTime
            ? moment(inputEndTime).format(MomentDateFormat.DateTimeLocalInput)
            : '';

        const lowboyTimesheetContract = this.getLowboyTimesheetContract();

        lowboyTimesheetContract.endTime = endTime;

        this.signalChange(lowboyTimesheetContract);
    };

    onNotesChange = (e) => {
        const notes = e && e.target ? e.target.value : null;

        const lowboyTimesheetContract = this.getLowboyTimesheetContract();

        lowboyTimesheetContract.notes = notes;

        this.signalChange(lowboyTimesheetContract);
    };

    onAdjustmentNotesChange = (e) => {
        const adjustmentNotes = e && e.target ? e.target.value : null;

        const lowboyTimesheetContract = this.getLowboyTimesheetContract();

        lowboyTimesheetContract.adjustmentNotes = adjustmentNotes;

        this.signalChange(lowboyTimesheetContract);
    };

    onAdjustedHoursSpentChange = (e) => {
        const adjustedHoursSpent = e && e.target ? e.target.value : null;

        const lowboyTimesheetContract = this.getLowboyTimesheetContract();

        lowboyTimesheetContract.adjustedHoursSpent = adjustedHoursSpent;

        this.signalChange(lowboyTimesheetContract);
    };

    signalChange = (lowboyTimesheetContract) => {
        const { onTimeUpdated } = this.props;

        onTimeUpdated(lowboyTimesheetContract);
    };

    getLowboyTimesheetContract = () => {
        const { lowboyTimesheet, contractId } = this.props;

        return lowboyTimesheet.lowboyTimesheetContracts.find(
            (ltc) => ltc.contractId == contractId
        );
    };

    isEndTimeInvalid = () => {
        const lowboyTimesheetContract = this.getLowboyTimesheetContract();

        if (
            !lowboyTimesheetContract ||
            !lowboyTimesheetContract.endTime ||
            !lowboyTimesheetContract.startTime
        ) {
            return undefined;
        }

        return moment(lowboyTimesheetContract.endTime).isSameOrBefore(
            lowboyTimesheetContract.startTime,
            'minute'
        );
    };

    isDisabled = () => {
        const { saving, lowboyTimesheet } = this.props;

        const isValidStatus =
            lowboyTimesheet.timesheetStatusId == TimesheetStatus.New ||
            lowboyTimesheet.timesheetStatusId == TimesheetStatus.Rejected;

        return Boolean(saving || !isValidStatus);
    };

    areAdminInputsDisabled = () => {
        const { saving, lowboyTimesheet } = this.props;

        return Boolean(
            saving ||
            lowboyTimesheet.timesheetStatusId > TimesheetStatus.Submitted
        );
    };

    render() {
        const {
            loading,
            lowboyAssignments,
            contractId,
            isAdmin,
            isReadOnly,
            modifyingUsers,
        } = this.props;

        if (loading || !contractId) {
            return null;
        }

        const filteredAssignments = lowboyAssignments.filter(
            (la) => la.lowboyResource.contractId == contractId
        );

        const lowboyTimesheetContract = this.getLowboyTimesheetContract();
        const endTimeInvalid = this.isEndTimeInvalid();
        const disabled = this.isDisabled();

        const adminInputDisabled = isReadOnly || this.areAdminInputsDisabled();

        const isValid = isLowboyTimesheetContractValid(lowboyTimesheetContract);

        const notesLabel = isAdmin ? 'User Notes' : 'Notes';

        const representativeLowboyResource =
            filteredAssignments[0].lowboyResource;

        return (
            <Card className="job-group mt-3">
                <CardHeader>
                    {representativeLowboyResource.contractName}

                    <AdjustmentDetail
                        entity={lowboyTimesheetContract}
                        modifyingUsers={modifyingUsers}
                        isAdmin={isAdmin}
                    />
                </CardHeader>
                {representativeLowboyResource.jobNotes && (
                    <CardSubtitle className="mt-2 ml-2">
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                        Notes from the PM:
                        <div>
                            <pre>{representativeLowboyResource.jobNotes}</pre>
                        </div>
                    </CardSubtitle>
                )}
                <CardBody className="d-flex">
                    <div
                        className={cls('w-25', 'ltc-entry', {
                            'ltc-invalid': !isValid,
                        })}
                    >
                        <FormGroup>
                            <FormLabel text="Start Time" />
                            <Input
                                type="datetime-local"
                                value={lowboyTimesheetContract.startTime ?? ''}
                                onChange={this.onStartTimeChange}
                                disabled={disabled}
                                readOnly={isReadOnly || isAdmin}
                                step={900}
                            />
                        </FormGroup>

                        <FormGroup>
                            <FormLabel text="End Time" />
                            <Input
                                type="datetime-local"
                                value={lowboyTimesheetContract.endTime ?? ''}
                                invalid={endTimeInvalid}
                                id={`ltc-${lowboyTimesheetContract.id}-end-time`}
                                onChange={this.onEndTimeChange}
                                color={endTimeInvalid ? 'danger' : ''}
                                disabled={disabled}
                                readOnly={isReadOnly || isAdmin}
                                step={900}
                            />

                            {endTimeInvalid && (
                                <div className="invalid-hint">
                                    <FontAwesomeIcon
                                        icon={faExclamationTriangle}
                                        className="mr-1"
                                    />
                                    End time must be greater than start time.
                                </div>
                            )}
                        </FormGroup>

                        <FormGroup>
                            <FormLabel text="Hours Spent" />

                            <Input
                                type="text"
                                value={lowboyTimesheetContract.hoursSpent ?? ''}
                                disabled={true}
                                readOnly={true}
                                invalid={
                                    lowboyTimesheetContract.hoursSpent &&
                                    lowboyTimesheetContract.hoursSpent < 0
                                }
                            />
                        </FormGroup>

                        <FormGroup>
                            <FormLabel text={notesLabel} />

                            <Input
                                type="textarea"
                                value={lowboyTimesheetContract.notes ?? ''}
                                disabled={disabled}
                                readOnly={isReadOnly || isAdmin}
                                onChange={this.onNotesChange}
                                maxLength={500}
                            />
                        </FormGroup>

                        {isAdmin && (
                            <>
                                <FormGroup>
                                    <FormLabel text="Adjusted Hours Spent" />

                                    <Input
                                        type="text"
                                        value={
                                            lowboyTimesheetContract.adjustedHoursSpent ??
                                            ''
                                        }
                                        disabled={adminInputDisabled}
                                        readOnly={adminInputDisabled}
                                        invalid={
                                            lowboyTimesheetContract.adjustedHoursSpent &&
                                            lowboyTimesheetContract.adjustedHoursSpent <
                                                0
                                        }
                                        onChange={
                                            this.onAdjustedHoursSpentChange
                                        }
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <FormLabel text="Adjustment Notes" />

                                    <Input
                                        type="textarea"
                                        value={
                                            lowboyTimesheetContract.adjustmentNotes ??
                                            ''
                                        }
                                        disabled={adminInputDisabled}
                                        onChange={this.onAdjustmentNotesChange}
                                        maxLength={500}
                                    />
                                </FormGroup>
                            </>
                        )}
                    </div>
                    <div className="flex-gap-3"></div>
                    <div className="w-75">
                        {filteredAssignments.map((la) => (
                            <LowboyTimesheetDetailCard
                                key={la.id}
                                lowboyAssignmentId={la.id}
                                {...this.props}
                            />
                        ))}
                    </div>
                </CardBody>
            </Card>
        );
    }
}
